import React, { createRef } from 'react';
import { useStratsContext } from '@/finsight/application/Strats/StratsContext';
import { CheckboxField } from '@/Framework/UI/Organisms/Filters/Checkboxer';
import { getRangeIdFromCheckboxName } from '@/finsight/ui/components/strats/helpers';
import { HeaderWrapper, HeaderRow, BodyWrapper } from '@dealroadshow/uikit/core/components/Table/DataTable';
import Checkbox from '@dealroadshow/uikit/core/components/Checkbox';
import Spinner from '@dealroadshow/uikit/core/components/Loader/Spinner';
import StratsRow from './StratsRow';
import { IField } from '@/Framework/UI/Organisms/Filters/Checkboxer/types';
import { IGroup, IStratsItem, IStratsRange } from '@/finsight/infrastructure/repository/StratsRepository/types';
import styles from './stratsTable.scss';
import alignStyles from '@dealroadshow/uikit/core/styles/helpers/align.scss';

interface IStratsTableProps {
  strats: IStratsItem,
  group: IGroup,
}

const StratsTable = ({
  group,
  strats,
}: IStratsTableProps) => {
  const checkboxParentFieldName = `checkbox-${ group.groupId }`;
  const checkbox = createRef<HTMLElement>();
  const { updatePopoverPosition, toggleRanges, metadata: { columns }, isStratsFetching } = useStratsContext();

  const mapColumns = (columnKey: string) => ({
    name: columnKey,
    title: columns[columnKey],
    className: alignStyles.alignRight,
  });

  const onToggleAll = (field: IField) => {
    toggleRanges(field.checked, field.children.map(getRangeIdFromCheckboxName));
    updatePopoverPosition(checkbox.current, checkboxParentFieldName);
  };

  const renderRows = (range: IStratsRange) => (
    <StratsRow
      key={ `${ checkboxParentFieldName }-${ range.rangeId }` }
      isDivider={ range.rangeDivider }
      ranges={ strats.ranges.map((range) => range.rangeId) }
      row={ {
        [checkboxParentFieldName]: range.rangeName,
        checkboxParentFieldName,
        checkboxFieldName: `${ checkboxParentFieldName }-${ range.rangeId }`,
        rangeId: range.rangeId,
        ...strats.data[range.rangeId],
      } }
    />
  );

  return (
    <div
      id={ `table-${ group.groupId }` }
      className={ styles.stratsTableContainer }
    >
      { (!strats || isStratsFetching) && (
        <Spinner overlay isVisible />
      ) }

      { strats && strats.ranges.length > 0 && (
        <table className={ styles.stratsTable }>
          <HeaderWrapper>
            <HeaderRow
              columns={ [
                {
                  name: checkboxParentFieldName,
                  /* @ts-ignore */
                  title: (
                    <div
                      id={ checkboxParentFieldName }
                      /* @ts-ignore */
                      ref={ checkbox }
                    >
                      <CheckboxField
                        defaultChecked
                        name={ checkboxParentFieldName }
                        dataTest={ checkboxParentFieldName }
                        component={ Checkbox }
                        supportIndeterminate
                        label={ group.groupName }
                        controlClassName={ styles.checkboxControl }
                        labelClassName={ styles.checkboxLabel }
                        onChange={ onToggleAll }
                      />
                    </div>
                  ),
                  minWidth: '270px',
                },
                ...Object.keys(columns).map(mapColumns),
              ] }
            />
          </HeaderWrapper>
          <BodyWrapper>
            { strats.ranges.map(renderRows) }
            <StratsRow
              isTotal
              ranges={ strats.ranges.map((range) => range.rangeId) }
              row={ {
                checkboxDisabled: true,
                onlyButtonDisabled: true,
                [checkboxParentFieldName]: 'Total:',
                checkboxParentFieldName,
                ...strats.total,
              } }
            />
          </BodyWrapper>
        </table>
      ) }
    </div>
  );
};

export default StratsTable;
